/* eslint-disable react-hooks/exhaustive-deps */
import React, { EffectCallback, ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useIdleTimer } from 'react-idle-timer'
import { RootState } from 'redux/rootReducer'
import {
  Container,
  SessionTimeOutHeader,
  SessionTimeOutModal
} from './subcomponents'
import SessionExpired from './SessionExpired'

const timeToOpenModal = 1000 * 60 * 12 // milliseconds
const timeToExpire = 180 // seconds

// eslint-disable-next-line sonarjs/cognitive-complexity
const SessionTimeOut = (): ReactElement => {
  const { isUserLoggedIn } = useSelector(({ appInfo }: RootState) => appInfo)

  const [isPageScrollDisabled, setIsPageScrollDisabled] =
    useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [secondsRemaining, setSecondsRemaining] = useState<number>(timeToExpire)
  const [intervalIdVal, setIntervalIdVal] = useState<NodeJS.Timeout>(null)
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false)

  const headerRef = React.useRef(null)

  const openModal = (): void => {
    setIsPageScrollDisabled(true)
    setIsModalOpen(true)
  }

  const closeModal = (): void => {
    setIsPageScrollDisabled(false)
    setSecondsRemaining(timeToExpire)
    clearInterval(intervalIdVal)
    setIsModalOpen(false)
    setIsSessionExpired(false)
  }

  const startExpiryTimer = (): void => {
    const intervalId = setInterval(() => {
      setSecondsRemaining((prevState: number) => prevState - 1)
    }, 1000)
    setIntervalIdVal(intervalId)
  }

  const { pathname } = useLocation()

  const { reset, pause } = useIdleTimer({
    timeout: timeToOpenModal,
    debounce: 500,
    onIdle: (): void => {
      if (isUserLoggedIn && pathname !== '/' && !isModalOpen) {
        pause()
        startExpiryTimer()
        openModal()
      }
    },
    onActive: (): void => {
      if (!isModalOpen) {
        reset()
        setSecondsRemaining(timeToExpire)
      }
    },
    onAction: (): void => {
      if (!isModalOpen) {
        reset()
      }
    }
  })

  const continueSession = (): void => {
    setIsPageScrollDisabled(false)
    clearInterval(intervalIdVal)
    reset()
    setSecondsRemaining(timeToExpire)
    setIsModalOpen(false)
  }

  const setWarning = (): void => {
    if (secondsRemaining === 0) {
      clearInterval(intervalIdVal)
      setIsSessionExpired(true)
    }
  }
  useEffect(setWarning, [secondsRemaining])

  const resetTimer = (): void => {
    if (isUserLoggedIn) {
      clearInterval(intervalIdVal)
      reset()
      setSecondsRemaining(timeToExpire)
    }
  }
  useEffect(resetTimer, [isUserLoggedIn])

  const updatePageScroll = (): EffectCallback => {
    document.body.style.overflow = isPageScrollDisabled ? 'hidden' : 'auto'

    // Ensure page scrolling is enabled on component unmount
    return (): void => {
      document.body.style.overflow = 'auto'
    }
  }
  useEffect(() => {
    updatePageScroll()
  }, [isPageScrollDisabled])

  return (
    <Container>
      <SessionTimeOutModal
        id="sessionTimeOutModal"
        headerRef={headerRef}
        isOpen={isModalOpen}
        aria-labelledby="sessionTimeOutModalTitle"
        aria-describedby="sessionTimeOutModalDescription"
        header={
          <SessionTimeOutHeader
            sessionHeaderRef={headerRef}
            closeModal={isSessionExpired ? closeModal : continueSession}
            timeLeft={secondsRemaining}
            isSessionExpired={isSessionExpired}
          />
        }
      >
        <SessionExpired
          isSessionExpired={isSessionExpired}
          loginHandler={closeModal}
          continueSessionHandler={continueSession}
        />
      </SessionTimeOutModal>
    </Container>
  )
}

export default SessionTimeOut
